import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { employeePayslipAPI } from '../services/employee.service';
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Title, Tooltip);

const PayrollPage = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [viewMode, setViewMode] = useState("payslip");
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [currentPayslip, setCurrentPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [requestNotifications, setRequestNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const empCode = localStorage.getItem('emp_code');
    if (empCode) {
      fetchPayslipData(empCode);
      fetchRequestNotifications();
    }
  }, []);

  useEffect(() => {
    const empCode = localStorage.getItem('emp_code');
    if (selectedMonth && empCode) {
      fetchPayslipDetails(empCode);
    }
  }, [selectedMonth]);

  const fetchRequestNotifications = async () => {
    try {
      const response = await employeePayslipAPI.getMyPayslipRequests();
      if (response && response.data) {
        const requests = response.data.data || response.data;
        // Filter for recent approved/rejected requests
        const notifications = requests.filter(req => 
          req.status === 'APPROVED' || req.status === 'REJECTED'
        ).slice(0, 5); // Show last 5 notifications
        setRequestNotifications(notifications);
      }
    } catch (error) {
      console.error('Error fetching request notifications:', error);
    }
  };

  const fetchPayslipData = async (empCode) => {
    try {
      setLoading(true);
      const response = await employeePayslipAPI.getPayslipHistory(12); // Get 12 months history
      if (response.data.success) {
        setPayrollHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching payslip data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslipDetails = async (empCode) => {
    try {
      const [year, month] = selectedMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long' });
      
      console.log(`Fetching payslip details for ${monthName} ${year}...`);
      const response = await employeePayslipAPI.getPayslipDetails(monthName, parseInt(year));
      
      if (response && response.data) {
        console.log(`API Response for ${monthName} ${year}:`, response.data);
        
        if (response.data.success === true) {
          console.log(`✅ Payslip found for ${monthName} ${year}`);
          setCurrentPayslip(response.data.data);
        } else if (response.data.success === false) {
          console.log(`❌ Payslip not available for ${monthName} ${year}:`, response.data.error);
          if (response.data.employee_info) {
            setCurrentPayslip({
              employee: response.data.employee_info,
              period: response.data.period,
              earnings: { basic: 0, hra: 0, special_allowance: 0, variable_pay: 0, gross: 0 },
              deductions: { pf: 0, professional_tax: 0, income_tax: 0, total: 0 },
              net_pay: 0,
              tax_regime: "NEW",
              payslip_available: false
            });
          } else {
            setCurrentPayslip(null);
          }
        } else if (response.data.employee && response.data.earnings) {
          console.log(`✅ Payslip found for ${monthName} ${year} (direct data)`);
          setCurrentPayslip(response.data);
        } else {
          console.log(`❌ Unexpected response structure for ${monthName} ${year}`);
          setCurrentPayslip(null);
        }
      } else {
        console.error('No response received from API');
        setCurrentPayslip(null);
      }
    } catch (error) {
      console.error('Error fetching payslip details:', error);
      setCurrentPayslip(null);
    }
  };

  const monthOptions = useMemo(() => {
    const options = [];
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const currentYear = new Date().getFullYear();
    
    // Generate options for current year and previous year
    for (let y = currentYear - 1; y <= currentYear + 1; y++) {
      for (let m = 1; m <= 12; m++) {
        const monthVal = m.toString().padStart(2, '0');
        const label = `${months[m - 1]} ${y}`;
        options.push({ label, value: `${y}-${monthVal}` });
      }
    }
    return options.reverse(); // Show recent months first
  }, []);

  const getLast6Months = (month) => {
    const [year, m] = month.split('-').map(Number);
    const months = [];
    for (let i = 5; i >= 0; i--) {
      let prevM = m - i;
      let prevY = year;
      if (prevM < 1) { prevM += 12; prevY--; }
      months.push(`${prevY}-${prevM.toString().padStart(2, '0')}`);
    }
    return months;
  };

  const isPayslipAvailable = currentPayslip !== null && currentPayslip.payslip_available !== false;

  const currentData = currentPayslip ? {
    gross: currentPayslip.earnings?.gross || 0,
    net: currentPayslip.net_pay || 0,
    tax: currentPayslip.deductions?.income_tax || 0,
    pf: currentPayslip.deductions?.pf || 0,
    professional_tax: currentPayslip.deductions?.professional_tax || 200,
    insurance: 0,
    loan: 0,
    deductions: currentPayslip.deductions?.total || 0,
    basic: currentPayslip.earnings?.basic || 0,
    special: currentPayslip.earnings?.special_allowance || 0,
    bonus: currentPayslip.earnings?.variable_pay || 0,
    hra: currentPayslip.earnings?.hra || 0
  } : {
    gross: 0, net: 0, tax: 0, pf: 0, professional_tax: 0, insurance: 0, loan: 0, deductions: 0, basic: 0, special: 0, bonus: 0, hra: 0
  };

  const pastMonths = payrollHistory.filter(p => new Date(p.month + '-01') <= new Date(selectedMonth + '-01'));
  const numMonths = pastMonths.length;
  const ytdGross = pastMonths.reduce((sum, p) => sum + (p.gross || 0), 0);
  const ytdTax = pastMonths.reduce((sum, p) => sum + (p.income_tax || 0), 0);
  const ytdPf = pastMonths.reduce((sum, p) => sum + (p.pf || 0), 0);

  const pieData = {
    labels: ['Net Pay', 'Income Tax', 'PF', 'Professional Tax', 'Other Deductions'],
    datasets: [{
      data: [
        currentData.net, 
        currentData.tax, 
        currentData.pf, 
        currentData.professional_tax,
        Math.max(0, currentData.deductions - currentData.tax - currentData.pf - currentData.professional_tax)
      ],
      backgroundColor: ['#10B981', '#EF4444', '#6366F1', '#F59E0B', '#8B5CF6'],
      borderWidth: 0,
      hoverOffset: 10,
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'right', 
        labels: { 
          font: { family: 'Roboto', size: 11, weight: '500' }, 
          color: '#4B5563', 
          padding: 12 
        } 
      },
    },
    cutout: '75%',
  };

  const last6 = getLast6Months(selectedMonth);
  const trendLabels = last6.map(m => new Date(m + '-01').toLocaleString('default', { month: 'short' }));
  const trendGross = last6.map(m => payrollHistory.find(p => p.month === m)?.gross || 0);
  const trendNet = last6.map(m => payrollHistory.find(p => p.month === m)?.net || 0);

  const barData = {
    labels: trendLabels,
    datasets: [
      { label: 'Gross Pay', data: trendGross, backgroundColor: '#1E293B' },
      { label: 'Net Pay', data: trendNet, backgroundColor: '#3B82F6' },
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top', labels: { font: { family: 'Roboto', size: 12 } } } },
    scales: { y: { beginAtZero: true, grid: { color: '#E5E7EB' } }, x: { grid: { display: false } } },
  };

  const handleBulkDownload = async () => {
    try {
      setDownloadLoading(true);
      const availableMonths = payrollHistory
        .filter(p => p.available !== false)
        .slice(0, 6);
      
      if (availableMonths.length === 0) {
        alert('No payslips available for download.');
        return;
      }

      console.log('Starting bulk download for months:', availableMonths);
      
      for (const payslip of availableMonths) {
        const [year, month] = payslip.month.split('-');
        const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long' });
        
        try {
          console.log(`Downloading ${monthName} ${year}...`);
          const response = await employeePayslipAPI.downloadPayslipPDF(monthName, parseInt(year));
          
          if (response?.success && response?.data?.download_url) {
            try {
              window.open(response.data.download_url, '_blank');
            } catch (error) {
              const link = document.createElement('a');
              link.href = response.data.download_url;
              link.download = `payslip_${monthName}_${year}.pdf`;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            console.warn(`No download URL for ${monthName} ${year}`);
            alert(`Failed to download payslip for ${monthName} ${year}`);
          }
        } catch (error) {
          console.error(`Failed to download ${monthName} ${year}:`, error);
          alert(`Error downloading payslip for ${monthName} ${year}`);
        }
      }
      
      alert(`Bulk download completed for ${availableMonths.length} payslips`);
    } catch (error) {
      console.error('Bulk download failed:', error);
      alert('Bulk download failed: ' + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSingleDownload = async () => {
    if (!isPayslipAvailable) {
      alert('Payslip not available for the selected month.');
      return;
    }

    try {
      setDownloadLoading(true);
      const [year, month] = selectedMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long' });
      
      console.log(`Downloading single payslip for ${monthName} ${year}...`);
      
      const response = await employeePayslipAPI.downloadPayslipPDF(monthName, parseInt(year));
      console.log('Download response:', response);
      console.log('Response structure:', {
        hasData: !!response?.data,
        hasSuccess: !!response?.data?.success,
        hasDataData: !!response?.data?.data,
        hasDownloadUrl: !!response?.data?.data?.download_url,
        responseData: response?.data
      });
      
      // The API returns: response.data.success and response.data.data.download_url
      if (response?.data?.success && response?.data?.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        console.log(`Download URL received: ${downloadUrl.substring(0, 100)}...`);
        
        // Simple direct download
        window.location.href = downloadUrl;
        
        alert('Payslip download started successfully!');
      } else {
        console.error('Download failed - Invalid response structure');
        console.error('Full response:', response);
        alert('Failed to download payslip. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response,
        responseData: error?.response?.data
      });
      alert('Download failed: ' + (error?.response?.data?.message || error?.message || 'Unknown error'));
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleRequestPayslip = async () => {
    try {
      setRequestLoading(true);
      const [year, month] = selectedMonth.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long' });
      
      console.log(`Requesting payslip for ${monthName} ${year}...`);
      const response = await employeePayslipAPI.requestPayslip(monthName.toUpperCase(), parseInt(year));
      
      console.log('Request payslip response:', response);
      
      if (response && response.success) {
        alert(`Payslip request submitted successfully for ${monthName} ${year}!`);
        // Refresh notifications after successful request
        setTimeout(() => fetchRequestNotifications(), 1000);
      } else {
        const errorMessage = response?.message || 'Failed to submit payslip request.';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Request failed:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert('Request failed: ' + error.response.data.message);
      } else {
        alert('Request failed: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Roboto:wght@400;500;600&display=swap');
          .font-heading { font-family: 'Poppins', sans-serif; }
          .font-body { font-family: 'Roboto', sans-serif; }
        `}
      </style>
      <div className="max-w-7xl mx-auto bg-white shadow-sm">
        {/* HEADER */}
        <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-md transition-all duration-200">
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold font-heading text-gray-800 uppercase tracking-wide">Payslip</h1>
            {/* Notification Bell */}
            {requestNotifications.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 hover:bg-gray-100 rounded-md transition-all duration-200 relative"
                >
                  <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h10a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {requestNotifications.length}
                  </span>
                </button>
                {showNotifications && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-3 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-800">Payslip Request Updates</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {requestNotifications.map((notification, index) => (
                        <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.month} {notification.year} Request {notification.status}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(notification.approved_at || notification.requested_at).toLocaleDateString()}
                              </p>
                              {(notification.remarks || notification.reason) && (
                                <p className="text-xs text-red-600 mt-1 bg-red-50 p-2 rounded">
                                  Reason: {notification.remarks || notification.reason}
                                </p>
                              )}
                            </div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              notification.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {notification.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium font-heading text-gray-600 uppercase">Period:</span>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded overflow-hidden">
              <button
                onClick={() => setViewMode("payslip")}
                className={`px-4 py-1.5 text-xs font-semibold uppercase transition-all duration-200 ${viewMode === 'payslip' ? 'bg-orange-600 text-white hover:bg-orange-700' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                Payslip
              </button>
              <button
                onClick={() => setViewMode("form16")}
                className={`px-4 py-1.5 text-xs font-semibold uppercase transition-all duration-200 ${viewMode === 'form16' ? 'bg-green-600 text-white hover:bg-green-700' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                Form 16
              </button>
            </div>
            <button
              onClick={handleSingleDownload}
              disabled={(viewMode === "payslip" && !isPayslipAvailable) || downloadLoading}
              className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold uppercase text-white rounded transition-all duration-200 ${
                viewMode === "form16" || isPayslipAvailable ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloadLoading ? 'Downloading...' : `Download ${viewMode === 'payslip' ? 'Payslip' : 'Form 16'}`}
            </button>
            {viewMode === "payslip" && !isPayslipAvailable && (
              <button
                onClick={handleRequestPayslip}
                disabled={requestLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold uppercase text-white bg-orange-600 hover:bg-orange-700 rounded transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {requestLoading ? 'Requesting...' : 'Request Payslip'}
              </button>
            )}
            <button
              onClick={handleBulkDownload}
              disabled={downloadLoading}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold uppercase text-white bg-blue-600 hover:bg-blue-700 rounded transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloadLoading ? 'Downloading...' : 'Download Last 6 Months'}
            </button>
          </div>
        </div>

        {viewMode === "payslip" ? (
          <>
            {/* EMPLOYEE INFO */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Employee Name</p>
                  <p className="font-semibold font-body text-gray-900 mt-0.5">
                    {currentPayslip?.employee?.name || 'Loading...'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Designation</p>
                  <p className="font-medium font-body text-gray-800 mt-0.5">
                    {currentPayslip?.employee?.designation || 'Loading...'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Department</p>
                  <p className="font-medium font-body text-gray-800 mt-0.5">
                    {currentPayslip?.employee?.department || 'Loading...'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Emp ID</p>
                  <p className="font-medium font-body text-gray-900 mt-0.5">
                    {currentPayslip?.employee?.emp_code || localStorage.getItem('emp_code') || 'Loading...'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Email</p>
                  <p className="font-medium font-body text-gray-800 mt-0.5">
                    {currentPayslip?.employee?.email || 'Loading...'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Tax Regime</p>
                  <p className="font-medium font-body text-gray-800 mt-0.5">
                    {currentPayslip?.tax_regime || 'NEW'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Pay Period</p>
                  <p className="font-medium font-body text-gray-800 mt-0.5">
                    {currentPayslip?.period || monthOptions.find(o => o.value === selectedMonth)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium font-heading text-gray-500 uppercase">Status</p>
                  <p className="font-medium font-body text-green-600 mt-0.5">
                    {isPayslipAvailable ? 'Generated' : 'Pending'}
                  </p>
                </div>
              </div>
            </div>

            {/* ANALYTICS SECTION */}
            <div className="p-6">
              <AnimatePresence mode="wait">
                {isPayslipAvailable ? (
                  <div className="space-y-6">
                    {/* Detailed Earnings & Deductions Table */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h3 className="text-base font-semibold font-heading text-gray-800 uppercase mb-4">
                        Detailed Pay Breakdown - {monthOptions.find(o => o.value === selectedMonth)?.label}
                      </h3>
                      <div className="grid grid-cols-2 gap-8">
                        {/* Earnings */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">EARNINGS</h4>
                          <table className="w-full text-sm">
                            <thead className="text-xs text-gray-600 uppercase">
                              <tr>
                                <th className="text-left py-1">Component</th>
                                <th className="text-right py-1">Standard</th>
                                <th className="text-right py-1">Monthly</th>
                                <th className="text-right py-1">YTD Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              <tr>
                                <td className="py-2">Basic Salary</td>
                                <td className="text-right font-medium">₹{currentData.basic.toLocaleString('en-IN')}</td>
                                <td className="text-right font-medium">₹{currentData.basic.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.basic * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                              <tr>
                                <td className="py-2">House Rent Allowance</td>
                                <td className="text-right font-medium">₹{currentData.hra.toLocaleString('en-IN')}</td>
                                <td className="text-right font-medium">₹{currentData.hra.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.hra * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                              <tr>
                                <td className="py-2">Special Allowance</td>
                                <td className="text-right font-medium">₹{currentData.special.toLocaleString('en-IN')}</td>
                                <td className="text-right font-medium">₹{currentData.special.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.special * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                              <tr>
                                <td className="py-2">Variable Pay</td>
                                <td className="text-right font-medium">₹{currentData.bonus.toLocaleString('en-IN')}</td>
                                <td className="text-right font-medium">₹{currentData.bonus.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.bonus * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                              <tr className="border-t-2 font-semibold bg-green-50">
                                <td className="py-2">GROSS PAY</td>
                                <td className="text-right">₹{currentData.gross.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{currentData.gross.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{ytdGross.toLocaleString('en-IN')}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Deductions */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">DEDUCTIONS</h4>
                          <table className="w-full text-sm">
                            <thead className="text-xs text-gray-600 uppercase">
                              <tr>
                                <th className="text-left py-1">Component</th>
                                <th className="text-right py-1">Amount</th>
                                <th className="text-right py-1">YTD</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              <tr>
                                <td className="py-2">Provident Fund</td>
                                <td className="text-right font-medium">₹{currentData.pf.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{ytdPf.toLocaleString('en-IN')}</td>
                              </tr>
                              <tr>
                                <td className="py-2">Professional Tax</td>
                                <td className="text-right font-medium">₹{currentData.professional_tax.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.professional_tax * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                              <tr>
                                <td className="py-2">Income Tax</td>
                                <td className="text-right font-medium">₹{currentData.tax.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{ytdTax.toLocaleString('en-IN')}</td>
                              </tr>
                              <tr>
                                <td className="py-2">Loss of Pay</td>
                                <td className="text-right font-medium">₹0.00</td>
                                <td className="text-right">₹0.00</td>
                              </tr>
                              <tr>
                                <td className="py-2">Other Deductions</td>
                                <td className="text-right font-medium">₹0.00</td>
                                <td className="text-right">₹0.00</td>
                              </tr>
                              <tr className="border-t-2 font-semibold bg-red-50">
                                <td className="py-2">TOTAL DEDUCTIONS</td>
                                <td className="text-right">₹{currentData.deductions.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.deductions * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                              <tr className="border-t-2 font-bold text-green-600 bg-green-100">
                                <td className="py-2">NET PAY</td>
                                <td className="text-right">₹{currentData.net.toLocaleString('en-IN')}</td>
                                <td className="text-right">₹{(currentData.net * numMonths).toLocaleString('en-IN')}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Pay Breakdown Chart */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h3 className="text-base font-semibold font-heading text-gray-800 uppercase mb-4">This Month's Pay Breakdown</h3>
                        <div className="flex flex-col lg:flex-row items-center gap-8">
                          <div className="w-56 h-56">
                            <Doughnut data={pieData} options={pieOptions} />
                          </div>
                          <div className="flex-1 flex flex-col items-center lg:items-start">
                            <div className="text-center mb-6">
                              <span className="text-xs font-medium font-heading text-gray-600 uppercase">Net Pay</span>
                              <div className="text-3xl font-bold font-body text-gray-900 mt-1">
                                ₹{currentData.net.toLocaleString('en-IN')}
                              </div>
                            </div>
                            <div className="text-sm font-body space-y-2">
                              <div className="flex items-center gap-3"><div className="w-3 h-3 bg-emerald-500 rounded-full"></div><span>Net Pay</span></div>
                              <div className="flex items-center gap-3"><div className="w-3 h-3 bg-red-500 rounded-full"></div><span>Income Tax</span></div>
                              <div className="flex items-center gap-3"><div className="w-3 h-3 bg-indigo-600 rounded-full"></div><span>PF</span></div>
                              <div className="flex items-center gap-3"><div className="w-3 h-3 bg-amber-500 rounded-full"></div><span>Professional Tax</span></div>
                              <div className="flex items-center gap-3"><div className="w-3 h-3 bg-purple-600 rounded-full"></div><span>Other Deductions</span></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Trend Chart */}
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h3 className="text-base font-semibold font-heading text-gray-800 uppercase mb-4">Last 6 Months Pay Trend</h3>
                        <div className="h-56">
                          <Bar data={barData} options={barOptions} />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="py-20 flex flex-col items-center justify-center">
                    <ClockIcon className="h-12 w-12 text-gray-400 mb-4" />
                    <h2 className="text-lg font-semibold font-heading text-gray-800">Payslip Not Available</h2>
                    <p className="text-gray-600 mt-2">The payslip for {monthOptions.find(o => o.value === selectedMonth)?.label} has not been generated yet.</p>
                    <p className="text-sm text-gray-500 mt-1">Please contact HR or request the payslip below.</p>
                    <button
                      onClick={handleRequestPayslip}
                      disabled={requestLoading}
                      className="mt-4 flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                      {requestLoading ? 'Requesting...' : 'Request Payslip'}
                    </button>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-10 font-body text-gray-900">
            <div className="text-center py-20">
              <p className="text-lg">Form 16 view – available at financial year end.</p>
            </div>
          </motion.div>
        )}

        {/* FOOTER */}
        <div className="bg-gray-100 px-6 py-3 text-center text-xs font-body text-gray-500 uppercase tracking-wider border-t border-gray-200">
          Confidential • Computer generated document • Ref: {viewMode === 'payslip' ? `${selectedMonth}-PAY` : 'FY-2024-25/F16'}
        </div>
      </div>
    </div>
  );
};

export default PayrollPage;